import { css } from 'styled-components';
import type { Styles } from 'generated/graphql';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useReporterStyles } from 'hooks/useReporterStyles';
import { telemetryContextFor } from 'modules/Apollo/types';
import { HeadingLevel } from '../../heading/constants';
import {
  getHeadingFontSize,
  getHeadingTextTransform,
  getHeadingTextDecoration,
} from '../../heading/utils/styling';

/**
 * RP-6005 (M2) — renders a `headingDecoration`-tagged label run with the user's
 * H1 reporter styles (matching `HeadingWrapper`) and emits `role="heading"` +
 * `aria-level` so the heading meaning survives for screen readers even though
 * nothing is stored. H2 is dropped, so styling always resolves H1.
 *
 * The outer `data-slate-leaf` span is added by the leaf pipeline; `getRenderLeaf`
 * spreads the slate attributes onto this component. `leaf`/`text` are kept off
 * the DOM.
 *
 * NOTE (RP-6005 follow-up): the H1 style helpers currently live in the heading
 * plugin; move them to a shared home (e.g. `useReporterStyles`) before the
 * heading plugin is deleted (RP-6012).
 */
type HeadingDecorationLeafProps = {
  children: React.ReactNode;
  leaf?: unknown;
  text?: unknown;
};

const HeadingDecorationLeaf = ({
  children,
  leaf: _leaf,
  text: _text,
  ...domProps
}: HeadingDecorationLeafProps): React.ReactElement => {
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterSettings = data?.me?.reporterSettings;

  const style: Styles = useReporterStyles(reporterSettings);
  const headingStyles = [...(style?.headingStyles ?? [])];

  const fontSize = getHeadingFontSize(headingStyles, HeadingLevel.H1);
  const textTransform = getHeadingTextTransform(headingStyles, HeadingLevel.H1);
  const textDecoration = getHeadingTextDecoration(headingStyles, HeadingLevel.H1);
  const fontFamily = style?.bodyStyle?.fontFamily;

  return (
    <span
      {...domProps}
      role="heading"
      aria-level={HeadingLevel.H1}
      css={css`
        text-transform: ${textTransform};
        font-family: ${fontFamily};
        font-size: ${fontSize}px;
        font-weight: ${textDecoration?.isBold === true ? 'bold' : 'normal'};
        font-style: ${textDecoration?.isItalic === true ? 'italic' : 'normal'};
        text-decoration: ${textDecoration?.isUnderline === true ? 'underline' : 'none'};
      `}
    >
      {children}
    </span>
  );
};

HeadingDecorationLeaf.displayName = 'HeadingDecorationLeaf';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HeadingDecorationLeaf);

export const HeadingDecorationEditable = HeadingDecorationLeaf;
export const HeadingDecorationRenderer = HeadingDecorationLeaf;
